import React, { useState, useEffect } from 'react';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FormInput, FormTextArea } from '../FormComponents';
import { requiredValidator } from '../Validators';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { filterBy } from '@progress/kendo-data-query';
import { Checkbox, RadioGroup } from '@progress/kendo-react-inputs';
import { FaSave, FaTrash } from 'react-icons/fa';
import { getGroupById, updateGroup, deleteGroup, Group, GroupType, GROUP_TYPE_OPTIONS } from '../../../data/groupData';
import { getEmployeeDropdown } from '../../../data/employeeData';
import { getUserRoles } from '../../../data/userData';
import { dropDown } from '../../../data/interfaces';
import { UserRoleOption } from '../../../data/userData';
import { useToast } from '../../../contexts/ToastContext';
import { getApiErrorDetails } from '../../../helpers/apiError';

interface EditGroupDetailsProps {
  groupId: number;
  onUpdate: () => void;
  onClose: () => void;
  canEdit?: boolean;
  isAdministrator?: boolean;
}

const EditGroupDetails: React.FC<EditGroupDetailsProps> = ({
  groupId,
  onUpdate,
  onClose,
  canEdit = false,
  isAdministrator = false,
}) => {
  const { showToast } = useToast();
  const [groupData, setGroupData] = useState<Group | null>(null);
  const [employees, setEmployees] = useState<dropDown[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<dropDown[]>([]);
  const [roles, setRoles] = useState<UserRoleOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [groupType, setGroupType] = useState<GroupType>('Security');

  useEffect(() => {
    loadGroupData();
    loadEmployees();
    loadRoles();
  }, [groupId]);

  const loadGroupData = async () => {
    try {
      const data = await getGroupById(groupId);
      console.log('Loaded group data:', data);
      console.log('Group roles:', data.roles);
      setGroupData(data);
      setSelectedEmployees(data.employees.map(e => e.employeeId));
      setSelectedRoles(data.roles.map(r => r.roleId));
      setGroupType(data.groupType ?? 'Security');
    } catch (error: any) {
      console.error('Error loading group:', error);
      showToast('Error loading group details', 'error');
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await getEmployeeDropdown("");
      setEmployees(data);
      setFilteredEmployees(data);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await getUserRoles();
      setRoles(data);
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  };

  const handleSave = async (values: any) => {
    try {
      console.log('=== BEFORE SAVE ===');
      console.log('Current selectedRoles:', selectedRoles);
      console.log('Saving group with data:', {
        groupId,
        name: values.name,
        description: values.description,
        employeeIds: selectedEmployees,
        roleIds: selectedRoles
      });
      
      await updateGroup({
        groupId: groupId,
        name: values.name,
        description: values.description || '',
        groupType,
        employeeIds: selectedEmployees.length > 0 ? selectedEmployees : null,
        // A distribution group grants no roles; the API drops any it had.
        roleIds: groupType === 'Security' && selectedRoles.length > 0 ? selectedRoles : null,
      });
      
      console.log('=== SAVE COMPLETE ===');
      showToast('Group updated successfully', 'success');
      await loadGroupData();
      onUpdate();
    } catch (error: any) {
      console.error('Error updating group:', error);
      showToast(getApiErrorDetails(error, 'Error updating group').message, 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteGroup(groupId);
      showToast('Group deleted successfully', 'success');
      setShowDeleteDialog(false);
      onClose();
      onUpdate();
    } catch (error: any) {
      console.error('Error deleting group:', error);
      showToast(getApiErrorDetails(error, 'Error deleting group').message, 'error');
    }
  };

  const handleEmployeeChange = (event: any) => {
    const values = event.target.value.map((item: dropDown) => item.value);
    setSelectedEmployees(values);
  };

  const handleEmployeeFilterChange = (event: any) => {
    const filtered = filterBy(employees, {
      logic: 'and',
      filters: [{
        field: 'text',
        operator: 'contains',
        value: event.filter.value
      }]
    });
    setFilteredEmployees(filtered);
  };

  if (!groupData) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  // A group that grants Portal.Administrator makes its members
  // administrators, so only an administrator may change it.
  const isAdminRole = (r: { area: string; permission: string }) =>
    `${r.area}.${r.permission}` === 'Portal.Administrator';
  const groupIsAdmin = groupData.roles.some(isAdminRole);
  const readOnly = !canEdit || (!isAdministrator && groupIsAdmin);
  const readOnlyReason = !canEdit
    ? 'You have view-only access to groups.'
    : 'Only a portal administrator can change a group that grants Portal.Administrator.';

  return (
    <div>

      <Form
        initialValues={{
          name: groupData.name,
          description: groupData.description,
        }}
        onSubmit={handleSave}
        render={(formRenderProps) => (
          <FormElement>
            <fieldset className={'k-form-fieldset'} disabled={readOnly}>
            {readOnly && <p className='k-form-hint'>{readOnlyReason}</p>}
            <Field
              name="name"
              component={FormInput}
              label="Group Name:"
              validator={requiredValidator}
              disabled={readOnly}
            />
            <Field
              name="description"
              component={FormTextArea}
              label="Description:"
              rows={3}
              disabled={readOnly}
            />

            <div className="k-form-field">
              <label className="k-label">Group type:</label>
              <RadioGroup
                data={GROUP_TYPE_OPTIONS}
                value={groupType}
                onChange={(e) => setGroupType(e.value)}
                disabled={readOnly}
              />
              {groupType === 'Distribution' && groupData.groupType === 'Security' && groupData.roles.length > 0 && (
                <p className='k-form-hint'>
                  Saving as a distribution group removes its {groupData.roles.length} role(s), and its
                  members lose any permissions they only had through this group.
                </p>
              )}
            </div>

            <div className="k-form-field">
              <label className="k-label">Employees:</label>
              <MultiSelect
                data={filteredEmployees}
                value={employees.filter(e => selectedEmployees.includes(e.value))}
                onChange={handleEmployeeChange}
                onFilterChange={handleEmployeeFilterChange}
                textField="text"
                dataItemKey="value"
                placeholder="Select employees..."
                filterable={true}
                disabled={readOnly}
                style={{ width: "100%" }}
              />
            </div>

            {groupType === 'Security' && (
            <div className="k-form-field">
              <label className="k-label">Roles:</label>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto', 
                border: '1px solid #ccc', 
                padding: '10px',
                borderRadius: '4px',
                backgroundColor: '#f9f9f9'
              }}>
                {roles
                  .sort((a, b) => {
                    const labelA = `${a.area} - ${a.permission}`;
                    const labelB = `${b.area} - ${b.permission}`;
                    return labelA.localeCompare(labelB);
                  })
                  .map(role => (
                  <div key={role.roleId} style={{ marginBottom: '8px' }}>
                    <Checkbox
                      value={selectedRoles.includes(role.roleId)}
                      disabled={readOnly || (!isAdministrator && isAdminRole(role))}
                      onChange={(e) => {
                        if (e.value) {
                          setSelectedRoles([...selectedRoles, role.roleId]);
                        } else {
                          setSelectedRoles(selectedRoles.filter(id => id !== role.roleId));
                        }
                      }}
                      label={`${role.area} - ${role.permission}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            )}

            </fieldset>

            {!readOnly && (
            <div className="button__container">
              <button 
                type="button" 
                className="form__edit__button edit"
                onClick={() => {
                  console.log('Save button clicked');
                  console.log('Form valid:', formRenderProps.valid);
                  console.log('Selected roles:', selectedRoles);
                  console.log('Selected employees:', selectedEmployees);
                  
                  // Get form values
                  const name = formRenderProps.valueGetter('name');
                  const description = formRenderProps.valueGetter('description');
                  
                  console.log('Form name:', name);
                  console.log('Form description:', description);
                  
                  // Call handleSave directly
                  handleSave({ name, description });
                }}
              >
                Save Changes
              </button>
              <button 
                type="button" 
                className="form__edit__button reject"
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete Group
              </button>
            </div>
            )}
          </FormElement>
        )}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <Dialog
          title="Confirm Delete"
          onClose={() => setShowDeleteDialog(false)}
        >
          <p>Are you sure you want to delete this group? This action cannot be undone.</p>
          <DialogActionsBar>
            <Button onClick={() => setShowDeleteDialog(false)} fillMode="outline">
              Cancel
            </Button>
            <Button onClick={handleDelete} themeColor="error">
              Delete
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default EditGroupDetails;
